import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { ShjLanguage } from '@speed-highlight/core';
import {
  FileIcon,
  TrashIcon,
  PlusIcon,
  PencilSimpleIcon,
} from '@phosphor-icons/react';
import { api, ApiError } from '~/lib/api-client';
import { moved, typed, useEditBuffer, without, type EditBuffer } from './file-edit-buffer';
import { Button } from '~/components/ui/button';
import { Skeleton } from '~/components/ui/skeleton';
import { CodeEditor } from '~/components/code-editor';
import { cn } from '~/lib/utils';
import { useConfirm } from '~/components/confirm-dialog';
import { FilePathDialog, folderOf, type FilePathDialogMode } from './file-path-dialog';

// FRONT-935: multi-file editor. Replaces the single-`index.html`
// RawContentEditor with a file tree + the shared CodeEditor over the
// granular /files CRUD routes (Task 2, src/admin/apps-routes.ts). Query
// keys are inline strings (this module follows edit-app.tsx's existing
// convention — apps don't have a query-keys.ts entry).
//
// Note on "make live": the new per-file PUT route has no `setLive` concept
// (unlike the old atomic `/content` route) — writing a file only updates
// `app_files`/`apps.bundled`. Per the plan's global constraint ("do not
// invent new publish semantics"), Save here is a plain write; publishing /
// going live stays whatever it already is elsewhere (Metadata tab status).

export interface AppFileMeta {
  path: string;
  contentType: string | null;
  sizeBytes: number | null;
  isBinary: boolean;
}

/** Extension → CodeEditor highlight language. Exported for direct testing. */
export function languageForPath(path: string): ShjLanguage {
  const ext = path.split('.').pop()?.toLowerCase() ?? '';
  switch (ext) {
    case 'css':
      return 'css';
    case 'js':
    case 'mjs':
    case 'cjs':
      return 'js';
    case 'json':
      return 'json';
    case 'html':
    case 'htm':
      return 'html';
    case 'svg':
    case 'xml':
      return 'xml';
    case 'py':
      return 'py';
    default:
      return 'plain';
  }
}

/** Structural files that must not be renamed/deleted from the editor — losing
 * them silently breaks the app: `index.html` (the required entry) and
 * `eddy.app.json` (the backend manifest, FRONT-965). Exported for testing. */
export function isProtectedFile(path: string): boolean {
  return path === 'index.html' || path === 'eddy.app.json';
}

function formatBytes(n: number | null): string {
  if (n == null) return 'Unknown size';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** One file as the editor reads it; `base` is the version a save sends back (apps that keep history). */
export interface AppFileContent {
  path: string;
  content: string;
  contentType: string | null;
  base?: string | null;
}

/** A file change's answer: `live: false` with a sentence when it is saved but not live yet. */
interface SaveAnswer {
  ok: boolean;
  live?: boolean;
  message?: string;
}

/** A save refused because the file changed on the app since the edit began. Exported for testing. */
export function isStaleRefusal(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409 && (error.body as { reason?: unknown } | undefined)?.reason === 'stale';
}

/** Saved and live, or saved with the sentence saying it isn't live yet. Exported for testing. */
export function savedToast(data: SaveAnswer | undefined, done: string): void {
  if (data?.live === false && data.message) toast.info(data.message);
  else toast.success(done);
}

/** The four file-mutations (save/create/rename/delete) plus their shared
 * toast/edits-buffer/query-invalidation side effects. Declaration order
 * (save, createFile, renameFile, deleteFile) matters — it's the order
 * `useMutation` gets invoked in, which files-editor.test.tsx asserts on. */
function useAppFileMutations({
  slug,
  selectedPath,
  setSelectedPath,
  setEdits,
  onSaved,
}: {
  slug: string;
  selectedPath: string | null;
  setSelectedPath: (path: string | null) => void;
  setEdits: (updater: (e: EditBuffer) => EditBuffer) => void;
  onSaved: () => void;
}) {
  const qc = useQueryClient();

  function afterMutation() {
    qc.invalidateQueries({ queryKey: ['admin-app-files', slug] });
    qc.invalidateQueries({ queryKey: ['admin-app', slug] });
    onSaved();
  }

  const save = useMutation({
    // Takes { path, content } explicitly (rather than closing over
    // `effectivePath`/`value`) so the mutation is a pure function of its
    // argument — testable by invoking it directly with arbitrary input, and
    // immune to stale-closure bugs: a PUT that resolves after the user has
    // switched to a different file must still toast/clean up the file it
    // was actually saving, not whatever is currently selected.
    // FRONT-2936: `base` is the version the edit started from (kept with the
    // edit), so a file that changed since is refused, never overwritten.
    mutationFn: ({ path, content, base }: { path: string; content: string; base?: string | null }) =>
      api.put<SaveAnswer>(`/api/admin/apps/${slug}/files/${path}`, base === undefined ? { content } : { content, base }),
    onSuccess: (data, { path }) => {
      savedToast(data, `Saved ${path}`);
      setEdits((e) => without(e, path));
      qc.invalidateQueries({ queryKey: ['admin-app-file', slug, path] });
      afterMutation();
    },
    // A stale save keeps the person's text and is answered in the editor, with a way to load the latest.
    onError: (e: Error) => {
      if (!isStaleRefusal(e)) toast.error(`Save failed: ${e.message}`);
    },
  });

  const createFile = useMutation({
    // `base: null`: a new file, so one that already exists is refused rather than emptied.
    mutationFn: (path: string) => api.put<SaveAnswer>(`/api/admin/apps/${slug}/files/${path}`, { content: '', base: null }),
    onSuccess: (data, path) => {
      savedToast(data, `Created ${path}`);
      setSelectedPath(path);
      afterMutation();
    },
    // No onError toast: the error shows inline in FilePathDialog (FRONT-2351).
  });

  const renameFile = useMutation({
    mutationFn: ({ from, to }: { from: string; to: string }) =>
      api.post<SaveAnswer>(`/api/admin/apps/${slug}/files/rename`, { from, to }),
    onSuccess: (data, { from, to }) => {
      savedToast(data, `Renamed to ${to}`);
      // Carry any unsaved edit over to the new path so an in-progress edit
      // follows the file instead of being silently dropped.
      setEdits((e) => moved(e, from, to));
      setSelectedPath(to);
      afterMutation();
    },
    // No onError toast: the error shows inline in FilePathDialog (FRONT-2351).
  });

  const deleteFile = useMutation({
    mutationFn: (path: string) => api.delete<SaveAnswer>(`/api/admin/apps/${slug}/files/${path}`),
    onSuccess: (data, path) => {
      savedToast(data, `Deleted ${path}`);
      if (selectedPath === path) setSelectedPath(null);
      setEdits((e) => without(e, path));
      afterMutation();
    },
    onError: (e: Error) => toast.error(`Delete failed: ${e.message}`),
  });

  return { save, createFile, renameFile, deleteFile };
}

/** Presentational left-sidebar file list: header + New button + rows with
 * the index.html-protected Rename/Delete actions. */
function FileList({
  files,
  filesLoading,
  effectivePath,
  onSelect,
  onNew,
  onRename,
  onDelete,
  readOnly,
}: {
  files: AppFileMeta[];
  filesLoading: boolean;
  effectivePath: string | null;
  onSelect: (path: string) => void;
  onNew: () => void;
  onRename: (path: string) => void;
  onDelete: (path: string) => void;
  readOnly: boolean;
}) {
  return (
    <div className="w-56 shrink-0 flex flex-col border-r border-border">
      <div className="flex items-center justify-between px-2 py-1.5 border-b border-border">
        <span className="text-xs font-medium text-muted-foreground">Files</span>
        {!readOnly && (
          <Button type="button" variant="ghost" size="icon-xs" aria-label="New file" onClick={onNew}>
            <PlusIcon size={14} />
          </Button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto">
        {filesLoading ? (
          <Skeleton className="h-24 w-full m-2" />
        ) : files.length === 0 ? (
          <p className="p-2 text-xs text-muted-foreground">No files.</p>
        ) : (
          files.map((f) => {
            const active = f.path === effectivePath;
            const isIndex = isProtectedFile(f.path);
            return (
              <div
                key={f.path}
                className={cn(
                  'group flex items-center justify-between gap-1 px-2 py-1 text-xs',
                  active && 'bg-accent',
                )}
              >
                <button
                  type="button"
                  className="flex items-center gap-1.5 truncate flex-1 min-w-0 text-left cursor-pointer"
                  onClick={() => onSelect(f.path)}
                >
                  <FileIcon size={12} className="shrink-0" />
                  <span className="truncate">{f.path}</span>
                </button>
                {!isIndex && !readOnly && (
                  <span className="hidden group-hover:flex group-focus-within:flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      aria-label={`Rename ${f.path}`}
                      className="cursor-pointer text-muted-foreground hover:text-foreground"
                      onClick={() => onRename(f.path)}
                    >
                      <PencilSimpleIcon size={12} />
                    </button>
                    <button
                      type="button"
                      aria-label={`Delete ${f.path}`}
                      className="cursor-pointer text-muted-foreground hover:text-destructive"
                      onClick={() => onDelete(f.path)}
                    >
                      <TrashIcon size={12} />
                    </button>
                  </span>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

/** `readOnly`: an editor of the app reads its files here; they change them in their own draft (FRONT-2937). */
export function FilesEditor({ slug, onSaved, readOnly = false }: { slug: string; onSaved: () => void; readOnly?: boolean }) {
  // null = "no explicit selection yet" — the effective selection derives
  // the first file from the tree below, so a fresh load lands on a file
  // immediately without needing an effect + extra render pass.
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  // Per-path unsaved-edit buffer. Keyed by path (not a single string) so
  // switching files doesn't require resetting/reseeding state via effects —
  // the displayed value is simply `edits[path] ?? fetchedContent`.
  const [edits, setEdits] = useEditBuffer();
  const qc = useQueryClient();

  const { data: filesData, isLoading: filesLoading } = useQuery({
    queryKey: ['admin-app-files', slug],
    queryFn: () => api.get<{ files: AppFileMeta[] }>(`/api/admin/apps/${slug}/files`),
  });
  const files = filesData?.files ?? [];
  const effectivePath = selectedPath ?? files[0]?.path ?? null;
  const selectedMeta = files.find((f) => f.path === effectivePath) ?? null;

  const { data: fileData, isLoading: fileLoading } = useQuery({
    queryKey: ['admin-app-file', slug, effectivePath],
    queryFn: () => api.get<AppFileContent>(`/api/admin/apps/${slug}/files/${effectivePath}`),
    enabled: !!effectivePath && !!selectedMeta && !selectedMeta.isBinary,
  });

  const pending = effectivePath ? edits[effectivePath] : undefined;
  const value = effectivePath ? (pending?.content ?? fileData?.content ?? '') : '';
  const dirty = !!pending && pending.content !== (fileData?.content ?? '');

  const { save, createFile, renameFile, deleteFile } = useAppFileMutations({
    slug,
    selectedPath,
    setSelectedPath,
    setEdits,
    onSaved,
  });

  const staleHere = !!effectivePath && isStaleRefusal(save.error) && save.variables?.path === effectivePath;

  /** Drops the person's text for this file and shows the app's current version of it. */
  function loadLatest() {
    if (!effectivePath) return;
    setEdits((e) => without(e, effectivePath));
    if (save.isError) save.reset();
    qc.invalidateQueries({ queryKey: ['admin-app-file', slug, effectivePath] });
  }

  const confirm = useConfirm();
  const [pathDialog, setPathDialog] = useState<FilePathDialogMode | null>(null);
  const [pathDialogError, setPathDialogError] = useState<string | null>(null);

  function openPathDialog(mode: FilePathDialogMode) {
    setPathDialogError(null);
    setPathDialog(mode);
  }

  function handlePathSubmit(path: string) {
    const opts = {
      onSuccess: () => setPathDialog(null),
      onError: (e: Error) => setPathDialogError(e.message),
    };
    setPathDialogError(null);
    if (pathDialog?.kind === 'rename') renameFile.mutate({ from: pathDialog.from, to: path }, opts);
    else createFile.mutate(path, opts);
  }

  async function handleDelete(path: string) {
    // `path in edits` is a good-enough "has an unsaved edit" signal here: the
    // buffer entry for a path is only ever cleared on successful save (or
    // delete), so its presence means there's a pending in-memory change.
    const unsavedNote = path in edits ? ' Your unsaved changes will also be discarded.' : '';
    const ok = await confirm({
      title: `Delete ${path}?`,
      description: `This cannot be undone.${unsavedNote}`,
      confirmText: 'Delete',
      destructive: true,
    });
    if (ok) deleteFile.mutate(path);
  }

  return (
    <div className="flex gap-4 h-full">
      <FileList
        files={files}
        filesLoading={filesLoading}
        effectivePath={effectivePath}
        onSelect={setSelectedPath}
        onNew={() => openPathDialog({ kind: 'new' })}
        onRename={(from) => openPathDialog({ kind: 'rename', from })}
        onDelete={(path) => void handleDelete(path)}
        readOnly={readOnly}
      />

      <FilePathDialog
        mode={pathDialog}
        existingPaths={files.map((f) => f.path)}
        defaultFolder={effectivePath ? folderOf(effectivePath) : ''}
        pending={createFile.isPending || renameFile.isPending}
        serverError={pathDialogError}
        onSubmit={handlePathSubmit}
        onClose={() => setPathDialog(null)}
      />

      <div className="flex-1 flex flex-col gap-2 min-w-0">
        {!effectivePath ? (
          <p className="text-sm text-muted-foreground">No files yet.</p>
        ) : selectedMeta?.isBinary ? (
          <div className="rounded-md border border-border p-3 text-sm space-y-1" data-testid="binary-info">
            <p className="font-medium">{effectivePath}</p>
            <p className="text-xs text-muted-foreground">{selectedMeta.contentType ?? 'Unknown type'}</p>
            <p className="text-xs text-muted-foreground">{formatBytes(selectedMeta.sizeBytes)}</p>
            <p className="text-xs text-muted-foreground">Binary file — cannot be edited here.</p>
          </div>
        ) : (
          <>
            <div className="flex-1 min-h-0">
              {fileLoading && value === '' ? (
                <Skeleton className="h-full w-full" />
              ) : (
                <CodeEditor
                  value={value}
                  readOnly={readOnly}
                  onChange={(next) => setEdits((e) => typed(e, effectivePath, next, fileData?.base))}
                  language={languageForPath(effectivePath)}
                  showLineNumbers
                  className="h-full"
                />
              )}
            </div>
            {staleHere && (
              <div role="alert" className="flex flex-wrap items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm">
                <span className="flex-1 min-w-0">
                  {effectivePath} changed in the app since you started editing it, so your change wasn&apos;t saved. Your text is still here.
                </span>
                <Button type="button" variant="outline" size="sm" onClick={() => void navigator.clipboard?.writeText(value)}>
                  Copy my text
                </Button>
                <Button type="button" size="sm" onClick={loadLatest}>
                  Load the latest
                </Button>
              </div>
            )}
            {!readOnly && (
              <div className="flex items-center justify-end gap-3 shrink-0 pt-3 border-t border-border">
                <Button
                  onClick={() => effectivePath && save.mutate({ path: effectivePath, content: value, base: pending?.base })}
                  disabled={!dirty || save.isPending}
                >
                  {save.isPending ? 'Saving…' : 'Save'}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
